import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import classNames from 'classnames';
import tracker from 'utils/tracker';
import {List as ImmutableList, Map as ImmutableMap} from 'immutable';

import {getDatasetId, getAuthSession} from 'utils/auth/selectors';
import {authLogout as authLogoutAction, authSelectDataset as authSelectDatasetAction} from 'utils/auth/actions';
import {AuthSelectDatasetPayload} from 'utils/auth/types';
import {getFeatureFlags} from 'utils/featureFlags/selectors';
import {createDialog as createDialogAction} from 'data/rootUi/actions';
import {
  DIALOG_TYPE as DIALOG_NOTIFICATION,
  mkNotificationDialogProps,
} from 'components/Dialogs/NotificationDialog/types';
import {doAdditionalDesktopFeatures as doAdditionalDesktopFeaturesAction} from 'components/Dialogs/WFDesktopFeatures/actions';
import {mkRootUiData} from 'data/rootUi/types';

import * as datasetSelectors from 'data/datasets/selectors';
import * as profileSelector from 'data/profile/selectors';
import * as subscriptionsSelectors from 'data/subscriptions/subscriptionsSelectors';

import {Config, ENVIRONMENT} from 'utils/webappConfig';

import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import CircularProgress from '@material-ui/core/CircularProgress';
import MiniDatasetPicker from 'components/MiniDatasetPicker';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import CheckIcon from '@material-ui/icons/Check';
import QPreferences from 'components/QPreferences';
import {isQuicken, isAcme} from 'isAcme';

import {overviewLayout, overviewLayoutVersion} from 'containers/OverviewPage/config';

class ProfileMenu extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      profileButtonAnchorEl: null,
      showDatasetId: false,
    };

    this.popoverActions = React.createRef()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.datasets !== this.props.datasets && Boolean(this.state.profileButtonAnchorEl)) {
      this.popoverActions.updatePosition();
    }
  }

  myAccountClick = () => {
    tracker.track(tracker.events.myAccount, {location: 'nav menu'});
    this.closeProfile();
    if (isAcme) window.open(`${Config.environments[ENVIRONMENT].myaccount_url}?hide-nav&bearer=${this.props.authSession.accessToken}`);
    else {
      window.open(`${Config.environments[ENVIRONMENT].myaccount_url}?bearer=${this.props.authSession.accessToken}`);
    }
  };

  logoutClick = () => this.props.authLogout({reason: 'AUTH_LOGOUT_USER_INITIATED'});

  openDesktopAdditionalFeatures = () => {
    this.closeProfile();
    this.props.doWFDesktopFeatures();
  };

  helpClick = () => {
    this.closeProfile();
    window.open('https://www.quicken.com/support');
  };

  handleDatasetClick = (dataset, classes) => {
    if (this.props.datasetId !== dataset.id) {
      tracker.track(tracker.events.datasetChange, {location: 'nav menu'});
      setTimeout(() => this.props.authSelectDataset(new AuthSelectDatasetPayload({dataset})), 100);
      this.closeProfile();
      this.props.createDialog(mkRootUiData({
        type: DIALOG_NOTIFICATION,
        allowNesting: false,
        props: ImmutableMap(mkNotificationDialogProps({
          buttonLabel: null,
          content: (
            <div className={classes.datasetSwitchingContent}>
              <Typography variant="body1">
                Switching to {dataset.name}
              </Typography>
              <CircularProgress classes={{root: classes.datasetSwitchingProgress}} size={50}/>
            </div>
          ),
          dialogProps: {
            disableBackdropClick: true,
            disableEscapeKeyDown: true,
          },
          title: '',
        })),
      }));
    } else {
      setTimeout(this.closeProfile, 100);  // the delay is only for feel to the user
    }
  };

  profileClick = (e) => {
    this.setState({profileButtonAnchorEl: e.currentTarget});
  };
  closeProfile = () => {
    this.setState({profileButtonAnchorEl: null});
  };
  handleProfileClose = () => {
    this.closeProfile();
  };

  getFirstLetterOfUserName = (profile) => {
    if (!profile) {
      return '';
    }
    const firstName = profile.firstName || (profile.primaryAddress ? profile.primaryAddress.fullName : '') || profile.username;
    return firstName[0].toUpperCase();
  };

  getFullName = (profile) => {
    if (!profile) {
      return '';
    }
    let fullName = '';
    if (profile.firstName && profile.lastName) {
      fullName = `${profile.firstName} ${profile.lastName}`;
    } else if (profile.primaryAddress && profile.primaryAddress.fullName) {
      fullName = profile.primaryAddress.fullName;
    }
    return fullName;
  };


  render() {
    const {classes, profile, subscription, financesEnabled, datasetId} = this.props;
    const {profileButtonAnchorEl} = this.state;

    let dataset = null;
    let datasets = null;
    if (this.props.datasets) {
      datasets = this.props.datasets;
      if(datasetId){
        dataset = datasets.get(datasetId);
        datasets = datasets.remove(datasetId);
        datasets = datasets.set(datasetId, dataset).reverse();
      }
    }
    const fullName = this.getFullName(profile);
    const overviewLayoutAcme = new ImmutableList(overviewLayout);

    return (
      <>
        <div
          id='profile-menu-bar'
          role='button'
          tabIndex={0}
          onKeyPress={() => null}
          onClick={this.profileClick}
          className={classNames(classes.navMenuItem)}>
          <Avatar className={classes.circleButton}>
            <div className={classes.firstNameLetter}>{this.getFirstLetterOfUserName(profile)}</div>
          </Avatar>
          <Typography style={{marginLeft: 24, textAlign: 'left'}} className={classes.navText}>
            Profile
          </Typography>
        </div>
        <Popover
          action={(actions) => {
            this.popoverActions = actions
          }}
          id="profile-menu"
          open={!!(profileButtonAnchorEl)}
          anchorEl={profileButtonAnchorEl}
          onClose={this.handleProfileClose}
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'bottom',
          }}
          transformOrigin={{
            horizontal: 'right',
            vertical: 'top',
          }}
          PaperProps={{
            style: {
              transform: 'translate3d(0, 0, 0)',  // fixes a scroll issue: https://github.com/mui-org/@material-ui/core/issues/10601
            },
          }}
          onKeyDown={(e) => {
            if (e.ctrlKey) {
              this.setState({showDatasetId: true});
            }
            if (e.key === 'Escape') {
              this.handleProfileClose();
            }
          }}
          onKeyUp={() => {
            this.setState({showDatasetId: false});
          }}
        >
          <div className={classes.profilePopover}>
            {profile &&
            <div className={classes.profileHeader}>
              <h3>{fullName}</h3>
              <p id="my-account-quicken-id">{profile.username}</p>
              {subscription && subscription.tierName && !isAcme &&
              <p id="my-account-product-tier">{subscription.tierName}</p>}
              {this.state.showDatasetId && dataset &&
              <p
                id="my-account-dataset-id">{`${dataset.id} - ${dataset.createdByClientId} -${dataset.platform}`}</p>}

              <Button
                variant="contained"
                onClick={this.myAccountClick}
                color="primary"
                id="my-account-button"
              >
                My Account
              </Button>
            </div>
            }
            {(datasets && datasets.size > 1) && (datasets || financesEnabled) &&
            <div className={classes.profileDataset}>
              <Typography color="inherit" variant="subtitle1" className={classes.profileFinancesTitle}>
                Finances
              </Typography>
              <List disablePadding>
                {datasets && datasets.valueSeq().map((theDataset, index) => {
                  if (index < 3) {
                    return (
                      <ListItem
                        button
                        key={theDataset.id}
                        onClick={() => this.handleDatasetClick(theDataset, classes)}
                        classes={{root: classes.datasetListItemDefault}}
                      >
                        <ListItemIcon
                          style={{
                            visibility: `${dataset === theDataset
                              ? 'visible'
                              : 'hidden'}`,
                            marginRight: 0,
                          }}
                        >
                          <CheckIcon/>
                        </ListItemIcon>
                        <ListItemText
                          primary={theDataset.name}
                          id={!index ? 'my-account-1st-data-set' : `my-account-data-set-${index + 1}`}
                        />
                      </ListItem>
                    );
                  }
                  return null;
                })}

                {datasets && datasets.size > 3 &&
                <MiniDatasetPicker
                  datasets={datasets}
                  dataset={dataset}
                  onChange={(d) => this.handleDatasetClick(d, classes)}
                  label="See all finances"
                  labelStyle={{label: classes.financesButtonStyle}}
                  id="mini-dataset-picker"
                />
                }
              </List>
            </div>
            }
            <MenuList>
              {isQuicken && dataset && dataset.createdByClientId === 'quicken_webapp' &&
              <MenuItem id="my-account-do-more-quicken" onClick={this.openDesktopAdditionalFeatures}>
                Do more with Quicken Desktop
              </MenuItem>
              }
              {this.props.profileHelpSupport &&
              < MenuItem
                onClick={this.helpClick}
                id="my-account-help-and-support">
                Help &amp; Support
              </MenuItem>
              }
              {this.props.intercomEnabled &&
              <MenuItem
                id="intercom-launcher"
                onClick={this.closeProfile}
              >
                Help
              </MenuItem>
              }
              {
                this.props.feedback &&
                <MenuItem
                  id="feedback-launcher"
                >
                  <a
                    href='https://community.simplifimoney.com/'
                    target='_blank'
                    style={{textDecoration: 'none', color: 'inherit', width: '100%', height: '100%'}}>
                    Feedback </a>
                </MenuItem>
              }

              {this.props.resetDashEnabled &&
              <MenuItem
                onClick={() => {
                  this.props.setDatasetPreference({overviewLayoutVersion, overviewPageLayout: overviewLayoutAcme});
                }}
                id="reset-default-dashboard"
              >
                Reset Dashboard Layout
              </MenuItem>}
              <MenuItem onClick={this.logoutClick} id="my-account-sign-out">Sign Out</MenuItem>
            </MenuList>
          </div>
        </Popover>
      </>
    );

  }
}

ProfileMenu.propTypes = {
  classes: PropTypes.object,
  datasetId: PropTypes.string,
  datasets: PropTypes.object,
  profile: PropTypes.object,
  feedback: PropTypes.bool,
  financesEnabled: PropTypes.bool,
  intercomEnabled: PropTypes.bool,
  resetDashEnabled: PropTypes.bool,
  subscription: PropTypes.object,
  profileHelpSupport: PropTypes.bool,
  authSelectDataset: PropTypes.func,
  createDialog: PropTypes.func,
  authSession: PropTypes.object,
  authLogout: PropTypes.func,
  setDatasetPreference: PropTypes.func,
  doWFDesktopFeatures: PropTypes.func,
};

const mapStateToProps = (state) => ({
  financesEnabled: getFeatureFlags(state).get('finances'),
  intercomEnabled: getFeatureFlags(state).get('intercom'),
  resetDashEnabled: getFeatureFlags(state).get('resetDefaultDashboard'),
  profileHelpSupport: getFeatureFlags(state).get('profileHelpSupport'),
  feedback: getFeatureFlags(state).get('feedback'),
  authSession: getAuthSession(state),
  datasets: datasetSelectors.getSortedDatasets(state),
  profile: profileSelector.getProfile(state),
  datasetId: getDatasetId(state),
  subscription: subscriptionsSelectors.getActiveSubscription(state),
});

const mapDispatchToProps = (dispatch) => ({
  createDialog: (uiRootData) => dispatch(createDialogAction(uiRootData)),
  authSelectDataset: (dataset) => dispatch(authSelectDatasetAction(dataset)),
  authLogout: (data) => dispatch(authLogoutAction(data)),
  doWFDesktopFeatures: () => dispatch(doAdditionalDesktopFeaturesAction()),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  QPreferences(),
)(ProfileMenu);
